/**
 * Robust userId extraction.
 *
 * The user object that lands in `localStorage.userData` (written by the
 * Vue → React handoff iframe at `public/auth-sync/sync-login.html`) has
 * a shape that varies between login providers:
 *
 *   • Google SSO        : { id, email, userDetail: {…} }
 *   • Password login    : { userDetail: { id, … }, … }
 *   • Vue ERP user      : { userDetail: { id, … }, provider, … }
 *
 * This resolver tries every reasonable key path, falls back to decoding
 * the JWT's `sub` claim, and logs the actual object shape when nothing
 * matches — so a future provider's unrecognised shape is visible in the
 * dev console instead of being a silent failure.
 */

import { jwtDecode } from 'jwt-decode';

import { AuthManager } from '../config/auth.config';
import { EMULATION_FLAG, STORAGE_KEYS } from '../constants/storageKeys';
import { logger } from './logger';

/**
 * Order matters — most specific first. Generic UUID-ish keys ahead of the
 * email-y ones (`sub` is the JWT subject which on most providers is the
 * email, not the user UUID).
 */
const ID_KEYS: ReadonlyArray<string> = [
  'userId',
  'id',
  'user_id',
  'uuid',
  'uid',
  '_id',
  'sub',
];

/**
 * Strict 8-4-4-4-12 hex UUID. Anything looser (e.g. "any string with a
 * dash") was a real bug: the Google OAuth `picture` field
 * (`https://lh3.googleusercontent.com/a/ACg8oc…s96-c`) matched a loose
 * heuristic and got POSTed to `/v2/users/composer/permission/{picture}`.
 * Reverting to a real UUID regex eliminates that whole class of bug.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUuid = (value: unknown): value is string =>
  typeof value === 'string' && UUID_RE.test(value);

const pickFromObject = (
  source: Record<string, unknown> | null | undefined,
): string | null => {
  if (!source) return null;
  for (const key of ID_KEYS) {
    const v = source[key];
    if (isUuid(v)) return v;
  }
  return null;
};

interface JwtPayload {
  sub?: string;
  uuid?: string;
  userId?: string;
  user_id?: string;
}

/**
 * Decode the active JWT and try to pull a UUID-shaped claim from it.
 * Returns null when the token is absent / malformed / contains only an
 * email-shaped `sub`.
 */
const extractFromJwt = (): string | null => {
  const token = AuthManager.getToken();
  if (!token) return null;
  try {
    const payload = jwtDecode<JwtPayload>(token);
    return (
      pickFromObject(payload as unknown as Record<string, unknown>) ?? null
    );
  } catch (err) {
    logger.warn('[userId] JWT decode failed', err);
    return null;
  }
};

/**
 * Try every shape we know of in this priority:
 *   1. Direct keys on the passed user object
 *   2. Nested `userDetail.*`
 *   3. Nested `user.*`            (some providers double-nest)
 *   4. JWT claim                  (last-ditch — usually email but worth a shot)
 *
 * Returns null silently on a miss. The diagnostic for an unrecognised shape
 * is owned by `resolveUserId` so it fires once per genuine failure rather
 * than once per fallback source.
 */
const pickFromNested = (
  source: Record<string, unknown>,
  nestedKey: string,
): string | null => {
  const nested = source[nestedKey];
  if (nested && typeof nested === 'object') {
    return pickFromObject(nested as Record<string, unknown>);
  }
  return null;
};

const describeNestedKeys = (
  source: Record<string, unknown>,
  nestedKey: string,
): string[] | null => {
  const nested = source[nestedKey];
  if (nested && typeof nested === 'object') {
    return Object.keys(nested as Record<string, unknown>);
  }
  return null;
};

export const extractUserId = (user: unknown): string | null => {
  if (!user || typeof user !== 'object') {
    return extractFromJwt();
  }
  const top = user as Record<string, unknown>;

  return (
    pickFromObject(top)
    ?? pickFromNested(top, 'userDetail')
    ?? pickFromNested(top, 'user')
    ?? extractFromJwt()
  );
};

const describeUserShape = (
  source: Record<string, unknown>,
): {
  topKeys: string[];
  userDetailKeys: string[] | null;
  userKeys: string[] | null;
} => ({
  topKeys: Object.keys(source),
  userDetailKeys: describeNestedKeys(source, 'userDetail'),
  userKeys: describeNestedKeys(source, 'user'),
});

/**
 * Emit a single diagnostic when no source yielded a UUID — but only if at
 * least one source was a real object. Null/absent sources mean auth is still
 * hydrating (the handoff hasn't written `userDetail` / `userData` yet), which
 * is a normal transient state and must stay silent. A non-null object with no
 * recognisable id is the genuine "unrecognised provider shape" worth surfacing.
 */
const warnUnresolvedIdentity = (
  sources: ReadonlyArray<readonly [string, unknown]>,
): void => {
  const inspectable = sources.filter(
    ([, value]) => value !== null && typeof value === 'object',
  );
  if (inspectable.length === 0) return;
  logger.warn(
    '[userId] could not extract — no source held a UUID-shaped id',
    Object.fromEntries(
      inspectable.map(([name, value]) => [
        name,
        describeUserShape(value as Record<string, unknown>),
      ]),
    ),
  );
};

/**
 * Read `localStorage.userDetail` — the backend's user record with the
 * canonical UUID. Vue ERP writes this under `STORE_USERDETAIL` on login;
 * the React app's handoff also lands it here (separate from `userData`
 * which carries the OAuth provider profile).
 */
const readUserDetailFromStorage = (): unknown => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.userDetail);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Read the emulated userId when `localStorage.isEmulate === 'true'`.
 * Returns null when emulation is OFF or the UUID is missing/malformed.
 *
 * Vue parity — `RW-ERP-Service/src/stores/permissions/user-permission.ts`
 * lines 49–56:
 *
 *   if (isEmulatedUser && isEmulatedUser.toString() == 'true') {
 *     userId = emulatedUserId;
 *   } else {
 *     userId = JSON.parse(userAuthDetail).userId;
 *   }
 *
 * Putting this check FIRST in the resolution order means every
 * downstream consumer (PermissionContext, useCurrentUserId, dependency
 * loader, page-level userId-keyed effects) automatically swaps identity
 * the moment emulation toggles — no extra wiring needed.
 */
const readEmulatedUserId = (): string | null => {
  try {
    const flag = localStorage.getItem(STORAGE_KEYS.isEmulate);
    if (flag !== EMULATION_FLAG.ON) return null;
    const raw = localStorage.getItem(STORAGE_KEYS.emulatedUserId);
    return isUuid(raw) ? raw : null;
  } catch {
    return null;
  }
};

/**
 * One-shot resolution. Priority order — emulation wins, then `userDetail`
 * (the canonical backend UUID in the Vue→React handoff), then the
 * `user` arg / OAuth profile / JWT fallbacks.
 *
 *   0. EMULATION                  (isEmulate=true → emulatedUserId)
 *   1. `localStorage.userDetail`  (backend record — has `userId` UUID)
 *   2. `user` arg                 (reactive useAuth().user — same shape as userData)
 *   3. `localStorage.userData`    (OAuth profile fallback)
 *   4. JWT `sub` claim            (last resort — usually email, rarely UUID)
 */
export const resolveUserId = (user: unknown): string | null => {
  const emulated = readEmulatedUserId();
  if (emulated) return emulated;

  const sources: ReadonlyArray<readonly [string, unknown]> = [
    ['userDetail', readUserDetailFromStorage()],
    ['userArg', user],
    ['userData', AuthManager.getUser()],
  ];

  for (const [, value] of sources) {
    const id = extractUserId(value);
    if (id) return id;
  }

  warnUnresolvedIdentity(sources);
  return null;
};
